import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { useTranslation } from 'react-i18next';

import { PollyLanguage } from '@shared/interfaces/polly';
import type { ScriptStep } from '@shared/interfaces/assistantScripts';
import AssistantScriptsService from '@shared/services/AssistantScriptsService';

import { findMissingLanguages, isLanguageComplete } from './scriptData';
import { useAiInspectorStyles } from './inspectorStyles';

const DEFAULT_LANGUAGES: PollyLanguage[] = [
  PollyLanguage.ENGLISH,
  PollyLanguage.SPANISH,
  PollyLanguage.FRENCH,
  PollyLanguage.SWEDISH,
];

interface ITranslationsBarProps {
  steps: ScriptStep[];
  baseLanguage: PollyLanguage | undefined;
  availableLanguages: PollyLanguage[] | undefined;
  // Called with the new steps when translations are filled in. Caller is
  // responsible for persisting the result (so the bar stays presentational
  // and easy to test).
  onApplyTranslations: (next: ScriptStep[]) => Promise<void> | void;
}

const TranslationsBar = ({
  steps,
  baseLanguage,
  availableLanguages,
  onApplyTranslations,
}: ITranslationsBarProps) => {
  const { t: translate } = useTranslation();
  const classes = useAiInspectorStyles();

  const effectiveBase = baseLanguage ?? PollyLanguage.ENGLISH;
  const languages = useMemo(() => {
    const seed = availableLanguages?.length ? availableLanguages : DEFAULT_LANGUAGES;
    if (seed.includes(effectiveBase)) {
      return seed;
    }
    return [effectiveBase, ...seed];
  }, [availableLanguages, effectiveBase]);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const missing = useMemo(
    () => findMissingLanguages(steps, languages, effectiveBase),
    [steps, languages, effectiveBase],
  );

  const handleTranslateMissing = async () => {
    if (busy || missing.length === 0 || steps.length === 0) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const sourceTexts = steps.map((s) => s.text ?? '');
      const { translations } = await AssistantScriptsService.translateScript({
        languages: missing,
        texts: sourceTexts,
      });
      const next: ScriptStep[] = steps.map((step, index) => {
        const merged = { ...(step.translations ?? {}) };
        for (const lang of missing) {
          const langTexts = translations[lang];
          if (Array.isArray(langTexts) && typeof langTexts[index] === 'string') {
            merged[lang] = langTexts[index];
          }
        }
        return { ...step, translations: merged };
      });
      await onApplyTranslations(next);
    } catch (translateError) {
      console.error(
        `Failed to translate missing languages: ${
          (translateError as Error).message
        }`,
        { languages: missing },
      );
      setError(translate(
        'editorMode.aiTranslateFailed',
        'Translation failed — try again.',
      ));
    } finally {
      setBusy(false);
    }
  };

  if (steps.length === 0) {
    return null;
  }

  return (
    <Box sx={classes.translationsRow}>
      <Typography component="span" sx={classes.translationsLabel}>
        <PublicOutlinedIcon />
      </Typography>
      {languages.map((lang) => {
        const done = isLanguageComplete(steps, lang, effectiveBase);
        return (
          <Box
            key={lang}
            component="span"
            sx={[classes.langChip, done && classes.langChipDone]}
          >
            {done ? <CheckIcon sx={{ fontSize: 9 }} /> : null}
            {lang.toUpperCase()}
          </Box>
        );
      })}
      {missing.length > 0 ? (
        <Box
          component="button"
          type="button"
          sx={classes.linkButton}
          onClick={handleTranslateMissing}
          disabled={busy}
        >
          {busy
            ? translate(
                'editorMode.aiTranslateBusy',
                'Translating…',
              )
            : translate(
                'editorMode.aiTranslateMissing',
                'Translate missing…',
              )}
        </Box>
      ) : null}
      {error ? (
        <Typography component="span" sx={{ fontSize: '10.5px', color: '#f06d6d' }}>
          {error}
        </Typography>
      ) : null}
    </Box>
  );
};

export default TranslationsBar;
